import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { RuleGroupInternalType } from '@procureai/query-builder';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useConfirmationDialog } from '@procureai/uix';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SavingQueryInput, SavingsListQuery } from '@/generated/gql/graphql';
import { PageError } from '~/components/PageError/PageError';
import { OpenCloseProps } from '~/hooks';

import { CreateSavingDialog } from './components/CreateSavingDialog/CreateSavingDialog';
import { EditSavingDialog } from './components/EditSavingDialog/EditSavingDialog';
import { SavingsList } from './components/SavingsList/SavingsList';
import { DELETE_SAVING_MUTATION } from './SavingsWidget.mutation.gql.ts';
import { SAVINGS_LIST_QUERY } from './SavingsWidget.query.gql';
import { getSavingsQueryVariable } from './utils/normalizeQueryBuilderFilter.ts';

type SavingsListRow = SavingsListQuery['savings'][number];

type SavingsWidgetProps = {
  query: RuleGroupInternalType | undefined;
  queryLoading: boolean;
  createDialogProps: Pick<OpenCloseProps, 'open' | 'onClose'>;
  selectedSavingId: string | null;
  onSelectedSavingIdChange: (id: string | null) => void;
};

export const SavingsWidget = ({
  query,
  queryLoading,
  createDialogProps,
  selectedSavingId,
  onSelectedSavingIdChange,
}: SavingsWidgetProps) => {
  const { t } = useTranslation();
  const { confirm } = useConfirmationDialog();
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const client = useApolloClient();
  const { hasPermission } = usePermissions();
  const canModify = hasPermission(Permission.CmdiSavingEdit);

  const savingsQueryVariable = useMemo<SavingQueryInput | undefined>(() => getSavingsQueryVariable(query), [query]);

  const { data, loading, error } = useQuery(SAVINGS_LIST_QUERY, {
    variables: { query: savingsQueryVariable },
  });

  const rows = useMemo<SavingsListRow[]>(() => data?.savings ?? [], [data]);

  const [deleteSaving] = useMutation(DELETE_SAVING_MUTATION, { onError });

  const handleEdit = useCallback(
    (id: string) => {
      onSelectedSavingIdChange(id);
    },
    [onSelectedSavingIdChange],
  );

  const handleDelete = useCallback(
    (id: string, name: string) => {
      confirm(
        async () => {
          const result = await deleteSaving({ variables: { id } });

          if (!result.errors?.length && result.data?.deleteSaving) {
            enqueueSuccessSnackbar(t('Savings.deleteDialog.success'));
            await client.refetchQueries({ include: [SAVINGS_LIST_QUERY] });
          }
        },
        {
          title: t('Savings.deleteDialog.title'),
          message: t('Savings.deleteDialog.message', {
            name: name || t('Savings.deleteDialog.thisSaving'),
          }),
        },
      )();
    },
    [client, confirm, deleteSaving, enqueueSuccessSnackbar, t],
  );

  const handleCloseEditDialog = useCallback(
    (open: boolean) => {
      if (!open) {
        onSelectedSavingIdChange(null);
      }
    },
    [onSelectedSavingIdChange],
  );

  return (
    <>
      {error ? (
        <PageError>{t('Savings.states.error')}</PageError>
      ) : (
        <SavingsList
          rows={rows}
          // Show the loading overlay only until the first page of data lands.
          // Both `loading` (Apollo) and `queryLoading` (encoded-query decode,
          // a non-network flag that is never cleared from the URL) can be true
          // while rows are already cached. Gating every loading signal by
          // `!data` keeps the grid from getting stuck behind the overlay when a
          // dialog opens or an encoded query lingers, while still showing the
          // overlay on the initial load and on a filter change (which clears
          // `data` for the new variables).
          loading={(loading || queryLoading) && !data}
          emptyStateText={t('Savings.states.empty')}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canModify={canModify}
        />
      )}

      <CreateSavingDialog {...createDialogProps} />
      <EditSavingDialog open={Boolean(selectedSavingId)} onOpenChange={handleCloseEditDialog} id={selectedSavingId} />
    </>
  );
};
